import { useState } from 'react'
import { FormControlLabel, Stack, TableBody, TableHead, TableRow } from '@mui/material'

import { Button, Card, Skeleton, Switch } from '@repo/ui'
import { SelectField } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useCanAccess } from 'hooks'
import { useSnackbar } from 'contexts'
import { usePullGap, useSeoGaps, useSetGapStatus } from 'hooks/api'
import type { SeoCompetitor, SeoTaxonomyTag } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, shortDate } from '../shared'
import { fmt } from './seoModel'
import { CompetitorMenu, KeywordMenu, ReviewChip, useCompetitorLookup } from './seoReview'
import { TagFilters, useTagFilters } from './seoTagFilters'

/**
 * Keywords a competitor ranks for that Vera does not track yet. A pull spends SE Ranking
 * credits, so it is manual and admin-only; on the trial plan the API returns at most
 * `rowCap` rows per pull, which the card says before you press the button.
 */
export const SeoGapsCard = ({
  market,
  competitors,
  taxonomy,
}: {
  market: string
  competitors: SeoCompetitor[]
  taxonomy: SeoTaxonomyTag[]
}) => {
  const tags = useTagFilters()
  const { data, isLoading, error } = useSeoGaps(market)
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const pull = usePullGap()
  const setGap = useSetGapStatus()
  const { open: notify } = useSnackbar()
  const lookup = useCompetitorLookup(competitors)
  // Only APPROVED competitors are mined. Business + search first; adjacent ones are listed
  // after them, so mining one is always an explicit choice.
  // Most threatening first (risk rank 1 = highest), unknown risk last.
  const approved = competitors
    .filter((c) => c.status === 'approved')
    .sort((a, b) => (a.profile?.riskRank ?? 99) - (b.profile?.riskRank ?? 99))
  const mineable = [
    ...approved.filter((c) => c.kind !== 'adjacent'),
    ...approved.filter((c) => c.kind === 'adjacent'),
  ]
  const [competitor, setCompetitor] = useState<string>(
    approved.find((c) => c.kind === 'business')?.domain ?? mineable[0]?.domain ?? '',
  )
  const [overlap, setOverlap] = useState(true)

  return (
    <Card title="Gap finder">
      <Stack spacing={4} sx={{ mt: 4 }}>
        {data?.trial ? (
          <Notice severity="warning">
            SE Ranking trial: each pull returns at most {data.rowCap} rows, so a competitor&rsquo;s
            list here is a sample of its best terms, not all of them. A pull costs about{' '}
            {fmt(data.costPerPull)} credits, plus 100 to look for similar sites.
          </Notice>
        ) : null}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
          <SelectField
            value={competitor}
            onChange={(v) => setCompetitor(v as string)}
            options={mineable.map((c) => ({
              value: c.domain,
              label: `${c.name ? `${c.name} · ${c.domain}` : c.domain}${
                c.kind === 'adjacent' ? ' (adjacent)' : c.kind === 'search' ? ' (search)' : ''
              }`,
            }))}
            placeholder="Competitor"
          />
          <FormControlLabel
            sx={{ mr: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            control={
              <Switch
                checked={overlap}
                onChange={(_: unknown, checked: boolean) => setOverlap(checked)}
                inputProps={{ 'aria-label': 'Also find similar sites' }}
              />
            }
            label="Also find similar sites (+100 credits)"
          />
          <Button
            variant="secondary"
            disabled={!isAdmin || !competitor || pull.isPending}
            disabledReason={
              !isAdmin
                ? 'Only platform admins can pull competitor keywords (it spends credits).'
                : undefined
            }
            loading={pull.isPending}
            onClick={() => pull.mutate({ competitor, market, overlap })}
          >
            Pull keywords
          </Button>
        </Stack>
        {pull.data ? (
          <SectionNote>
            {pull.data.competitor}: {fmt(pull.data.rows)} rows for {pull.data.credits} credits,{' '}
            {fmt(pull.data.keywordsProposed)} proposed as keywords for review.
            {pull.data.warning ? ` ${pull.data.warning}` : ''}
            {pull.data.overlap
              ? 'error' in pull.data.overlap
                ? ` Similar sites: not run (${pull.data.overlap.error}).`
                : ` Similar sites: ${
                    pull.data.overlap.proposed.length + pull.data.overlap.reproposed.length
                      ? `proposed ${[...pull.data.overlap.proposed, ...pull.data.overlap.reproposed].join(', ')}`
                      : 'nothing new to propose'
                  } (see Competitors).`
              : ''}
          </SectionNote>
        ) : null}
        {pull.error ? (
          <Notice severity="error">
            {(pull.error as Error).message}
          </Notice>
        ) : null}

        <TagFilters taxonomy={taxonomy} state={tags.state} onChange={tags.setState} />
        {isLoading ? (
          <Skeleton height={14} lines={8} />
        ) : error || !data ? (
          <SectionNote>Could not load gap keywords: {(error as Error | null)?.message}</SectionNote>
        ) : (
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>Keyword</HeadCell>
                <HeadCell>Competitor</HeadCell>
                <HeadCell align="right">Their rank</HeadCell>
                <HeadCell align="right">Volume</HeadCell>
                <HeadCell align="right">KD</HeadCell>
                <HeadCell align="right">
                  Score
                  <InfoTooltip term="seo-gap-score" />
                </HeadCell>
                <HeadCell>Pulled</HeadCell>
                <HeadCell>Keyword</HeadCell>
                <HeadCell align="right"> </HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.length === 0 ? (
                <EmptyRow colSpan={9} text="Nothing pulled for this market yet." />
              ) : (
                data.rows.filter((r) => tags.matches(r)).slice(0, 100).map((r) => {
                  const owner = lookup(r.competitor)
                  const status = r.alreadyTracked ? 'approved' : r.keywordStatus
                  return (
                    <TableRow key={r.id}>
                      <Cell strong>{r.keyword}</Cell>
                      <Cell>
                        <Stack direction="row" alignItems="center" component="span">
                          {r.competitor}
                          <CompetitorMenu
                            domain={owner?.domain ?? r.competitor}
                            status={owner?.status ?? null}
                          />
                        </Stack>
                      </Cell>
                      <Cell align="right">{fmt(r.position)}</Cell>
                      <Cell align="right">{fmt(r.volume)}</Cell>
                      <Cell align="right">{fmt(r.kd)}</Cell>
                      <Cell align="right" strong>
                        {fmt(r.score)}
                      </Cell>
                      <Cell>{shortDate(r.fetchedAt)}</Cell>
                      <Cell>
                        {status === 'approved' ? 'Tracked' : status ? <ReviewChip status={status} /> : '—'}
                      </Cell>
                      <Cell align="right">
                        <KeywordMenu
                          keyword={r.keyword}
                          status={status ?? null}
                          pending={setGap.isPending}
                          onSet={(next) =>
                            setGap.mutate(
                              { id: r.id, status: next },
                              {
                                onSuccess: () =>
                                  notify({
                                    type: 'success',
                                    text:
                                      next === 'approved'
                                        ? `Added “${r.keyword}” to tracked keywords.`
                                        : `Rejected “${r.keyword}”.`,
                                  }),
                                onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                              },
                            )
                          }
                        />
                      </Cell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </StatTable>
        )}
      </Stack>
    </Card>
  )
}
